import { Grid, Select, Stack, Textarea, TextInput } from "@mantine/core";
import { regionApi } from "apis";
import { partnerApi } from "apis/partnerApi";
import { CreatableSelect, Loading } from "components";
import CustomText from "components/ui/CustomText/CustomText";
import { RegionResponse } from "constants/types/region";
import { FormikProps, getIn } from "formik";
import { memo, useState } from "react";
import { getInitials } from "utils/helpers/string";
import { useNotification } from "utils/hooks";
import { useCustomers } from "utils/hooks/request/usePartners";
import { useListRegion } from "utils/hooks/request/useRegions";
import { useUsers } from "utils/hooks/request/useUsers";
import { SOCreateFormValues, emptyPartner } from "../../config";

const spanHalf = { base: 12, md: 6 };

interface SalesOrderPartnerFieldsProps {
  formik: FormikProps<SOCreateFormValues>;
}

const SalesOrderPartnerFields = memo(
  ({ formik }: SalesOrderPartnerFieldsProps) => {
    const [isFetching, setIsFetching] = useState(false);
    // Đơn BÁN thì đối tác là khách hàng. Không dùng useListPartner() (type=both)
    // vì backend hiểu "both" là CHỈ đối tác gắn nhãn both — khách hàng thuần
    // type=customer sẽ không hiện ra trong danh sách chọn.
    const { customerOptions: partnerOptions } = useCustomers();
    const { regionOptions, mutate: mutateRegions } = useListRegion();
    const { userOptions } = useUsers();
    const { notification } = useNotification();

    const partner = formik.values.partner;
    const isExisting = !!partner.id;

    const handlePartnerSelect = async (partnerId: string | null) => {
      if (!partnerId) {
        formik.setFieldValue("partner", emptyPartner());
        formik.setTouched({ ...formik.touched, partner: {} as any });
        return;
      }
      setIsFetching(true);
      try {
        const res = await partnerApi.getById(partnerId);
        const p = res.data.data;
        formik.setFieldValue("partner", {
          id: partnerId,
          name: p.name ?? "",
          phone: p.phone ?? "",
          taxCode: p.taxCode ?? "",
          contactPersonId: p.contactPersonId ?? "",
          regionId: p.regionId ?? "",
          notes: p.notes ?? "",
        });
      } catch {
        // silently ignore – customer list is still available for retry
      }
      setIsFetching(false);
    };

    const fieldError = (field: string) => {
      if (isExisting) return null;
      const touched = getIn(formik.touched, `partner.${field}`);
      const error = getIn(formik.errors, `partner.${field}`);
      return touched && error ? (error as string) : null;
    };

    const contactPersonError = (() => {
      const touched = getIn(formik.touched, "partner.contactPersonId");
      const error = getIn(formik.errors, "partner.contactPersonId");
      return touched && error ? (error as string) : null;
    })();

    const idError = (() => {
      const touched = getIn(formik.touched, "partner.id");
      const error = getIn(formik.errors, "partner.id");
      return touched && error ? (error as string) : null;
    })();

    return (
      <Stack gap="sm" pos="relative">
        <CustomText variant="section-title">Thông tin khách hàng</CustomText>
        <Loading visible={isFetching} variant="overlay" />

        <Select
          label="Chọn khách hàng có sẵn"
          placeholder="Tìm kiếm khách hàng..."
          searchable
          clearable
          data={partnerOptions ?? []}
          value={partner.id || null}
          onChange={handlePartnerSelect}
          onBlur={() => formik.setFieldTouched("partner.id", true)}
          error={idError}
        />

        <Grid>
          <Grid.Col span={spanHalf}>
            <TextInput
              label="Tên khách hàng"
              placeholder="Nhập tên khách hàng"
              withAsterisk={!isExisting}
              disabled={isExisting}
              value={partner.name}
              onChange={(e) =>
                formik.setFieldValue("partner.name", e.target.value)
              }
              onBlur={() => formik.setFieldTouched("partner.name", true)}
              error={fieldError("name")}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <TextInput
              label="Số điện thoại"
              placeholder="Nhập số điện thoại"
              disabled={isExisting}
              value={partner.phone}
              onChange={(e) =>
                formik.setFieldValue("partner.phone", e.target.value)
              }
              onBlur={() => formik.setFieldTouched("partner.phone", true)}
              error={fieldError("phone")}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <TextInput
              label="Mã số thuế"
              placeholder="Nhập mã số thuế"
              withAsterisk={!isExisting}
              disabled={isExisting}
              value={partner.taxCode}
              onChange={(e) =>
                formik.setFieldValue("partner.taxCode", e.target.value)
              }
              onBlur={() => formik.setFieldTouched("partner.taxCode", true)}
              error={fieldError("taxCode")}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <CreatableSelect
              label="Người phụ trách"
              placeholder="Chọn người phụ trách"
              // Khác các ô còn lại: KHÔNG khoá khi chọn khách có sẵn. Giá trị
              // này lưu riêng trên đơn, đổi ở đây không sửa hồ sơ khách hàng.
              allowCreate={false}
              data={userOptions ?? []}
              value={partner.contactPersonId || null}
              onChange={(value) =>
                formik.setFieldValue("partner.contactPersonId", value ?? "")
              }
              onBlur={() =>
                formik.setFieldTouched("partner.contactPersonId", true)
              }
              error={contactPersonError}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <CreatableSelect
              label="Địa bàn"
              placeholder="Chọn địa bàn"
              withAsterisk={!isExisting}
              disabled={isExisting}
              data={regionOptions}
              value={partner.regionId || null}
              onChange={(value) =>
                formik.setFieldValue("partner.regionId", value)
              }
              onBlur={() => formik.setFieldTouched("partner.regionId", true)}
              error={fieldError("regionId")}
              onCreate={async (query) => {
                try {
                  const res = await regionApi.create({
                    name: query,
                    code: getInitials(query),
                  });
                  const data: RegionResponse = res.data.data;
                  notification.success({
                    title: `Tạo địa bàn "${query}" thành công`,
                  });
                  mutateRegions();
                  return { label: data.name, value: data.id };
                } catch (error: any) {
                  notification.error({
                    title: "Tạo địa bàn thất bại",
                    message: error?.error?.message,
                  });
                  throw error;
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Textarea
              label="Ghi chú"
              placeholder="Nhập ghi chú (tuỳ chọn)"
              autosize
              minRows={2}
              disabled={isExisting}
              value={partner.notes}
              onChange={(e) =>
                formik.setFieldValue("partner.notes", e.target.value)
              }
              error={fieldError("notes")}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    );
  },
  (prev, next) =>
    prev.formik.values.partner === next.formik.values.partner &&
    prev.formik.errors.partner === next.formik.errors.partner &&
    prev.formik.touched.partner === next.formik.touched.partner,
);

SalesOrderPartnerFields.displayName = "SalesOrderPartnerFields";

export default SalesOrderPartnerFields;
